import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Edit2, Users, Share2, X as XIcon, Play, Maximize2, Minimize2 } from 'lucide-react';
import { Project, Staff } from '../types';
import { EditProjectModal } from './EditProjectModal';
import { supabase } from '../lib/supabase';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import Plyr from 'plyr-react';
import "plyr-react/plyr.css";

interface ProjectModalProps {
  project: Project;
  onClose: () => void;
  onUpdate: (updatedProject: Project) => void;
  isNew?: boolean;
}

interface ImageCache {
  [key: string]: {
    aspectRatio: number;
    loaded: boolean;
  };
}

const workloadLabels: { [key: string]: string } = {
  'very_high': 'Very High',
  'high': 'High',
  'moderate': 'Moderate',
  'low': 'Low',
  'very_low': 'Very Low'
};

export function ProjectModal({ project, onClose, onUpdate, isNew = false }: ProjectModalProps) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isEditing, setIsEditing] = useState(isNew);
  const [allStaff, setAllStaff] = useState<Staff[]>([]);
  const [loading, setLoading] = useState(false);
  const [imageCache, setImageCache] = useState<ImageCache>({});
  const [showShareTooltip, setShowShareTooltip] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    loadStaff();
    if (project.images?.length > 0) {
      project.images.forEach((image, index) => {
        if (image.media_type === 'image') {
          preloadImage(image.url, index);
        }
      });
    }
  }, []);

  const getYouTubeThumbnail = (url: string) => {
    const videoId = url.match(/embed\/([^?]+)/)?.[1];
    return videoId ? `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg` : '';
  };

  const preloadImage = (url: string, index: number) => {
    if (imageCache[url]?.loaded) return;

    const img = new Image();
    img.onload = () => {
      setImageCache(prev => ({
        ...prev,
        [url]: {
          aspectRatio: img.naturalWidth / img.naturalHeight,
          loaded: true
        }
      }));
    };
    img.src = url;
  };

  const loadStaff = async () => {
    try {
      const { data: staffData, error: staffError } = await supabase
        .from('staff')
        .select('id, name');

      if (staffError) throw staffError;

      const loadedStaff: Staff[] = staffData;
      setAllStaff(loadedStaff);
    } catch (error) {
      console.error('Error loading staff:', error);
    }
  };

  const nextImage = () => {
    if (!project.images?.length) return;
    const nextIndex = currentImageIndex === project.images.length - 1 ? 0 : currentImageIndex + 1;
    const nextNextIndex = nextIndex === project.images.length - 1 ? 0 : nextIndex + 1;
    const nextImage = project.images[nextNextIndex];
    if (nextImage && nextImage.media_type === 'image') {
      preloadImage(nextImage.url, nextNextIndex);
    }
    setCurrentImageIndex(nextIndex);
  };

  const previousImage = () => {
    if (!project.images?.length) return;
    const prevIndex = currentImageIndex === 0 ? project.images.length - 1 : currentImageIndex - 1;
    const prevPrevIndex = prevIndex === 0 ? project.images.length - 1 : prevIndex - 1;
    const prevImage = project.images[prevPrevIndex];
    if (prevImage && prevImage.media_type === 'image') {
      preloadImage(prevImage.url, prevPrevIndex);
    }
    setCurrentImageIndex(prevIndex);
  };

  const handleProjectUpdate = (updatedProject: Project) => {
    onUpdate(updatedProject);
    setIsEditing(false);
  };

  const handleShare = () => {
    const shareUrl = `${window.location.origin}/share?projectId=${project.id}`;
    navigator.clipboard.writeText(shareUrl);
    setShowShareTooltip(true);
    setTimeout(() => setShowShareTooltip(false), 2000);
  };

  const currentImage = project.images?.[currentImageIndex];
  const currentAspectRatio = currentImage?.media_type === 'image' && currentImage.url ? imageCache[currentImage.url]?.aspectRatio : null;
  const isImageLoaded = currentImage?.media_type === 'image' && currentImage.url ? imageCache[currentImage.url]?.loaded : true;

  const getImageStyle = () => {
    if (!currentAspectRatio) return {};
    
    if (currentAspectRatio >= 0.95 && currentAspectRatio <= 1.05) {
      return {
        width: isFullscreen ? '90%' : '80%',
        height: isFullscreen ? '90%' : '80%',
        objectFit: 'contain'
      };
    }
    
    if (currentAspectRatio > 1.05) {
      return {
        width: '100%',
        height: 'auto',
        maxHeight: isFullscreen ? '95%' : '90%',
        objectFit: 'contain'
      };
    }
    
    return {
      width: 'auto',
      height: isFullscreen ? '95%' : '90%',
      maxWidth: isFullscreen ? '95%' : '90%',
      objectFit: 'contain'
    };
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const mediaSection = (
    <div className="relative bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center" style={{ height: isFullscreen ? '100vh' : '500px' }}>
      {currentImage ? (
        <>
          {currentImage.media_type === 'youtube' ? (
            <div 
              className="w-full h-full"
              key={`${currentImage.id}-youtube-${isFullscreen ? 'fs' : 'inline'}`}
            >
              <Plyr
                source={{
                  type: "video",
                  sources: [{
                    src: currentImage.url,
                    provider: "youtube"
                  }]
                }}
                options={{
                  controls: ['play-large', 'play', 'progress', 'current-time', 'mute', 'volume', 'fullscreen']
                }}
              />
            </div>
          ) : currentImage.media_type === 'video' ? (
            <div 
              className="w-full h-full"
              key={`${currentImage.id}-video-${isFullscreen ? 'fs' : 'inline'}`}
            >
              <Plyr
                source={{
                  type: "video",
                  sources: [{ src: currentImage.video_url || '', type: 'video/mp4' }]
                }}
                options={{
                  controls: ['play-large', 'play', 'progress', 'current-time', 'mute', 'volume', 'fullscreen']
                }}
              />
            </div>
          ) : isImageLoaded ? (
            <img
              src={currentImage.url}
              alt={`Project image ${currentImageIndex + 1}`}
              style={getImageStyle()}
              className="transition-all duration-200"
            />
          ) : null}

          {project.images.length > 1 && (
            <>
              <button
                onClick={previousImage}
                className="absolute left-4 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-70 text-white p-2 rounded-full transition-all"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button
                onClick={nextImage}
                className="absolute right-4 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-70 text-white p-2 rounded-full transition-all"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            </>
          )}

          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black bg-opacity-50 text-white px-3 py-1 rounded-full text-sm">
            {currentImageIndex + 1} / {project.images.length}
          </div>

          <button
            onClick={toggleFullscreen}
            className="absolute top-4 right-4 bg-black bg-opacity-50 hover:bg-opacity-70 text-white p-2 rounded-full transition-all"
          >
            {isFullscreen ? <Minimize2 className="w-6 h-6" /> : <Maximize2 className="w-6 h-6" />}
          </button>
        </>
      ) : (
        <p className="text-gray-500">No image available</p>
      )}
    </div>
  );

  if (isEditing) {
    return (
      <EditProjectModal
        project={project}
        onSave={handleProjectUpdate}
        onCancel={() => setIsEditing(false)}
        isNew={isNew}
        allStaff={allStaff}
      />
    );
  }

  if (isFullscreen && currentImage) {
    return (
      <div className="fixed inset-0 bg-black z-50 flex items-center justify-center">
        {mediaSection}
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden">
        <div className="p-6 border-b border-gray-200 relative">
          <div className="pr-32">
            <h2 className="text-2xl font-bold text-gray-900">{project.projectName}</h2>
            <div className="mt-2 flex items-center space-x-4 text-sm text-gray-500">
              <span>{project.customer.name}</span>
              {(project.month || project.year) && (
                <>
                  <span>•</span>
                  <span>
                    {project.month && `${project.month} `}{project.year}
                  </span>
                </>
              )}
              {project.duration && (
                <>
                  <span>•</span>
                  <span>{project.duration.name}</span>
                </>
              )}
              {project.budget && (
                <>
                  <span>•</span>
                  <span>{project.budget}</span>
                </>
              )}
              {project.workload && (
                <>
                  <span>•</span>
                  <span>{workloadLabels[project.workload]} Workload</span>
                </>
              )}
            </div>
          </div>
          <div className="absolute top-6 right-6 flex items-center space-x-2">
            <div className="relative">
              <button
                onClick={handleShare}
                className="text-gray-500 hover:text-gray-700 p-2 rounded-full hover:bg-gray-100"
              >
                <Share2 className="w-5 h-5" />
              </button>
              {showShareTooltip && (
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1 bg-gray-900 text-white text-sm rounded">
                  Link copied!
                  <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1">
                    <div className="border-x-4 border-x-transparent border-t-4 border-t-gray-900" />
                  </div>
                </div>
              )}
            </div>
            {!isNew && (
              <button
                onClick={() => setIsEditing(true)}
                className="text-gray-500 hover:text-gray-700 p-2 rounded-full hover:bg-gray-100"
              >
                <Edit2 className="w-5 h-5" />
              </button>
            )}
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 p-2 rounded-full hover:bg-gray-100"
            >
              <XIcon className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="overflow-y-auto" style={{ maxHeight: 'calc(90vh - 90px)' }}>
          <div className="p-6">
            {project.images?.length > 0 ? (
              <>
                <div className="mb-8">
                  {mediaSection}

                  {project.images.length > 1 && (
                    <div className="mt-4 grid grid-cols-4 sm:grid-cols-6 gap-2">
                      {project.images.map((image, index) => (
                        <button
                          key={image.id}
                          onClick={() => setCurrentImageIndex(index)}
                          className={`aspect-video rounded-lg overflow-hidden border-2 transition-all ${
                            currentImageIndex === index
                              ? 'border-indigo-500 opacity-100'
                              : 'border-transparent opacity-60 hover:opacity-100'
                          }`}
                        >
                          {image.media_type === 'youtube' ? (
                            <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                              <img
                                src={getYouTubeThumbnail(image.url)}
                                alt="YouTube thumbnail"
                                className="w-full h-full object-cover"
                              />
                            </div>
                          ) : image.media_type === 'video' ? (
                            <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                              <video
                                src={image.video_url}
                                className="w-full h-full object-cover"
                                preload="metadata"
                              />
                            </div>
                          ) : (
                            <img
                              src={image.url}
                              alt={`Thumbnail ${index + 1}`}
                              className="w-full h-full object-cover"
                            />
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="mb-8 aspect-video bg-gray-100 rounded-lg flex items-center justify-center">
                <p className="text-gray-500">No images available</p>
              </div>
            )}

            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-2">Project Description</h3>
              <div className="prose prose-sm max-w-none text-gray-600">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {project.description}
                </ReactMarkdown>
              </div>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-2">Tags</h3>
              <div className="flex flex-wrap gap-2">
                {project.tags?.map((tag) => (
                  <span
                    key={tag.id}
                    className="px-3 py-1 bg-indigo-100 text-indigo-800 rounded-full text-sm"
                  >
                    {tag.name}
                  </span>
                ))}
              </div>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-2">Project Team</h3>
              <div className="flex flex-wrap gap-2">
                {project.staff?.map((member) => (
                  <span
                    key={member.id}
                    className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-gray-100 text-gray-800"
                  >
                    <Users className="w-4 h-4 mr-1" />
                    {member.name}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}